import { IBoaAnalyticsOptions } from '@/interfaces/powerMarkets/balancing/IBoaAnalyticsOptions.ts';
import {
    IBoaItem, AcceptedVolumes, IBoaIdentificationProperties, BidOnTableType,
} from '@/interfaces/powerMarkets/balancing/IBoaItem.ts';
import { IBoaTableData } from '@/interfaces/powerMarkets/balancing/IBoaTableData.ts';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';

import { isValidObjectWithRequiredKeys } from '@/utils/isValidObjectWithRequiredKeys.ts';
import dayjs from 'dayjs';
import { computed, ComputedRef, Ref } from 'vue';
import cssVariables from '@/assets/cssVariables.json';
import { IBoaDashboardDataUpdate, IBoaStackData } from '@/interfaces/powerMarkets/balancing/IBoaPageData.ts';
import { getSelectedPlantIds } from '@/helpers/powerMarkets/plantGroupSettings.ts';

const DEFAULT_BID_COLOUR = cssVariables.boaColors.bid;
const DEFAULT_OFFER_COLOUR = cssVariables.boaColors.offer;

function getFlagType (flags: { 'TERRE': boolean; 'STOR': boolean; 'SO': boolean; 'CADL': boolean }): string {
    const flagsToShow = [];

    if (flags.TERRE) {
        flagsToShow.push('TERRE flag');
    }
    if (flags.STOR) {
        flagsToShow.push('STOR flag');
    }
    if (flags.SO) {
        flagsToShow.push('SO flag');
    }
    if (flags.CADL) {
        flagsToShow.push('CADL flag');
    }
    return flagsToShow.join(', ');
}

function getAssetId (boaItem: IBoaItem, addBSAD: boolean, plantDisplayName: string): string {
    if (isPlantBSAD(boaItem.plantId)) {
        const adder = addBSAD ? ' [BSAD]' : '';

        if (boaItem.additionalBsadData && boaItem.additionalBsadData.assetId) {
            return `${boaItem.additionalBsadData.assetId}${adder}`;
        } return `${boaItem.plantId.split('_')[1]}${adder}`;
    }
    return plantDisplayName;
}

function getBoaPlantDetailsId (boaItem: IBoaItem): string {
    return getAssetId(boaItem, false, boaItem.plantId);
}

function isPlantBSAD (plantId: string): boolean {
    return plantId.startsWith('BSAD');
}

function getTooltipInfo (row: IBoaItem): string | null {
    let content = '';
    if (row.tooltipOverride && row.tooltipOverride.trim() !== '') {
        content = row.tooltipOverride;
    } else {
        if (isPlantBSAD(row.plantId)) {
            if (isValidObjectWithRequiredKeys(row.additionalBsadData, ['serviceType', 'tenderedStatus', 'partyId'])) {
                const { serviceType, tenderedStatus, partyId } = row.additionalBsadData;
                content = [
                    serviceType,
                    tenderedStatus,
                    partyId,
                ].filter(Boolean).join(' - ');
            }
        }
        if (row.isMissingBsad) {
            if (content !== '') {
                content += '<br>';
            }
            content += 'This BSAD is missing from the initial Elexon settlement run. This might affect the ESO System Price.';
        }
    }
    if (content === '') return null;
    return content;
}

function formatCost (num: number): string {
    if (num === 0) return '0';
    if (Math.abs(num) >= 100) return num.toFixed(0);
    return num.toFixed(2).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
}

function formatBoaVolume (volume: number): string {
    if (!volume) return '0';
    return volume.toFixed(1);
}

function getBoaFormattedVolumeComparison (volumes: AcceptedVolumes): string {
    if (!volumes) {
        return '0';
    }

    const volumesList = [];

    if (volumes.unflagged) {
        volumesList.push(formatBoaVolume(volumes.unflagged));
    }
    if (volumes.soFlagged) {
        volumesList.push(formatBoaVolume(volumes.soFlagged));
    }
    if (volumes.storFlagged) {
        volumesList.push(formatBoaVolume(volumes.storFlagged));
    }
    if (volumes.cadlFlagged) {
        volumesList.push(formatBoaVolume(volumes.cadlFlagged));
    }

    if (volumesList.length === 1) {
        return volumesList[0];
    }

    if (volumesList.length === 0) {
        return '0';
    }

    return volumesList.join(' | ');
}

function getSyncFlagging (row: IBoaItem): 'Sync' | 'Desync' | null {
    if (row.sync) {
        if (row.enumType === 3 || row.enumType === 1) return 'Desync'; // Desync

        if (row.enumType === 0 || row.enumType === 2) return 'Sync'; // Sync
    }
    return null;
}

function addOrUpdateDatatableTooltips (tableElement: HTMLElement, classesToQuery: Array<string>, tableData: Array<IBoaItem>, tooltipStringCallback: (item: IBoaItem) => string | null): void {
    const elements = tableElement.querySelectorAll(`.${classesToQuery.join(', .')}`);
    elements.forEach((element) => {
        const uniqueIdString = ((element.querySelector('.plantId') as HTMLElement).children[0] as HTMLElement)?.classList[0];
        const [plantId, bodId, isBidString] = uniqueIdString.split('|');
        const isBid = isBidString === '1' ? 1 : 0;

        const boa = tableData.find((item) => item.plantId === plantId && item.bodId === bodId && item.isBid === isBid);
        if (!boa) return;

        const tooltipString = tooltipStringCallback(boa);
        if (!tooltipString) return;

        const exisitingLastChild = element.children[element.children.length - 1] as HTMLElement;
        if (exisitingLastChild.classList.contains('translucent-tooltip-box')) {
            const tooltipContent = exisitingLastChild.querySelector('tooltip-content') as HTMLElement;
            if (!tooltipContent) {
                exisitingLastChild.remove();
            } else {
                tooltipContent.innerText = tooltipString;
                return;
            }
        }

        // Add tooltip to each accepted BOA element
        const tooltip = document.createElement('div');
        tooltip.innerHTML = `
            <div class="tooltip-inner">
                <div class="tooltip-arrow"></div>
                <div class="tooltip-content">${tooltipString}</div>
            </div>
        `;
        tooltip.classList.add('translucent-tooltip-box');
        element.appendChild(tooltip);
    });
}

function datePeriodIsNow (datePeriodOne: IDatePeriod, datePeriodTwo: IDatePeriod): boolean {
    const dateOfNewData = dayjs(datePeriodOne.date);
    const currentDate = dayjs(datePeriodTwo.date);
    return datePeriodOne.period === datePeriodTwo.period && dateOfNewData.isSame(currentDate, 'day');
}

function mergeBoas (
    incoming: Array<IBoaItem>,
    target: Array<IBoaItem>,
    flashTimers: Record<string, ReturnType<typeof setTimeout>>,
    flashingIds: Set<string>,
): void {
    incoming.forEach(boa => {
        const i = target.findIndex(b => areBoasEqual(b, boa));
        if (i === -1) target.push(boa);
        else target[i] = boa;

        markBoasAsUpdated([boa], flashTimers, flashingIds);
    });
}

function markBoasAsUpdated (
    incoming: Array<IBoaItem>,
    flashTimers: Record<string, ReturnType<typeof setTimeout>>,
    flashingIds: Set<string>,
): void {
    incoming.forEach(boa => flashRow(`${boa.plantId}|${boa.bodId}|${boa.isBid}`, flashTimers, flashingIds));
}

function getBoaDashboardUpdateGroup (datePeriod: IDatePeriod): string {
    return `BoaDashboardSnapshot${dayjs(datePeriod.date).format('YYYY-MM-DD')}/${datePeriod.period}`;
}

function applyBoaDashboardUpdate (data: IBoaStackData, update: IBoaDashboardDataUpdate): boolean {
    // A sequence starting at zero can follow a Main API restart.
    if (update.item.revision === data.revision && update.item.previousRevision !== 0) return true;
    if (update.item.reloadRequired || data.revision !== update.item.previousRevision) return false;

    applyTableChanges(data.tableBids.data, update.item.tableBids, update.item.removedTableBids);
    applyTableChanges(data.tableOffers.data, update.item.tableOffers, update.item.removedTableOffers);
    data.tableBids.count = data.tableBids.data.length;
    data.tableOffers.count = data.tableOffers.data.length;
    data.tableBids.countNoBsads = data.tableBids.data.filter(item => !isPlantBSAD(item.plantId)).length;
    data.tableOffers.countNoBsads = data.tableOffers.data.filter(item => !isPlantBSAD(item.plantId)).length;

    applyTableChanges(data.acceptedBids, update.item.acceptedBids, update.item.removedAcceptedBids);
    applyTableChanges(data.acceptedOffers, update.item.acceptedOffers, update.item.removedAcceptedOffers);
    data.revision = update.item.revision;
    return true;
}

function applyTableChanges (target: Array<IBoaItem>, changes: Array<IBoaItem>, removed: Array<IBoaItem>): void {
    const removedKeys = new Set(removed.map(boaIdentity));
    for (let index = target.length - 1; index >= 0; index--) {
        if (removedKeys.has(boaIdentity(target[index]))) target.splice(index, 1);
    }

    changes.forEach(change => {
        const index = target.findIndex(item => boaIdentity(item) === boaIdentity(change));
        if (index === -1) target.push(change);
        else target[index] = change;
    });
}

function boaIdentity (boa: IBoaIdentificationProperties): string {
    if (boa.deltaId) return boa.deltaId;
    return `${boa.plantId}|${boa.bodId}|${boa.isBid}`;
}

function flashRow (
    boaUniqueString: string,
    flashTimers: Record<string, ReturnType<typeof setTimeout>>,
    flashingIds: Set<string>,
): void {
    if (flashTimers[boaUniqueString]) clearTimeout(flashTimers[boaUniqueString]);

    flashingIds.add(boaUniqueString);

    flashTimers[boaUniqueString] = setTimeout(() => {
        flashingIds.delete(boaUniqueString);
        delete flashTimers[boaUniqueString];
    }, 5000);
}

function filterAvailableBoasToDisplay (
    boas: Ref<IBoaTableData | null>,
    otherSettings: ISiteSettings | null,
    analyticsOptions?: Ref<IBoaAnalyticsOptions>,
): ComputedRef<IBoaTableData | null> {
    return computed(() => {
        if (!boas.value) return null;
        if (!otherSettings) return boas.value;

        const plantIdsToFilter = otherSettings.filterTrackedPlants ? new Set(getSelectedPlantIds(otherSettings)) : null;

        const filteredBoaData = boas.value.data.filter(value => {
            if (
                (value.isBid === 1 && parseInt(value.bodId, 10) > 0)
                || (value.isBid !== 1 && parseInt(value.bodId, 10) < 0)
            ) {
                let volumeAccepted = 0;
                if (value.volumeComparison !== null) volumeAccepted = value.volumeComparison;
                if (volumeAccepted !== 0 && otherSettings.filterAcceptedUnwinds) return false;
                if (volumeAccepted === 0 && otherSettings.filterUnacceptedUnwinds) return false;
            }

            if (plantIdsToFilter && !plantIdsToFilter.has(value.plantId)) return false;

            if (!analyticsOptions) return true;

            // all conditions below only when in analytics mode
            if (analyticsOptions.value.noticeDeviateFilter >= 0) {
                if (value.isBid === 1) {
                    if (value.ndb > analyticsOptions.value.noticeDeviateFilter) return false;
                } else {
                    if (value.bidOnTableTypeEnum === BidOnTableType.TurnOn && value.ndz > analyticsOptions.value.noticeDeviateFilter) {
                        return false;
                    }
                    if (value.ndo > analyticsOptions.value.noticeDeviateFilter) return false;
                }
            }

            if (value.volume === 0) return false;
            return true;
        });

        return {
            ...boas.value,
            data: filteredBoaData,
        };
    });
}

function getPeriodDataJson (boaData: IBoaStackData | null | undefined): Array<Record<string, string | number>> {
    if (!boaData || !boaData.tableBids || !boaData.tableOffers) return [];
    const bids = getExcelTable(boaData.tableBids.data, true);
    const offers = getExcelTable(boaData.tableOffers.data, false);

    return [...bids, ...offers];
}

function getExcelTable (data: Array<IBoaItem>, isBid: boolean): Array<Record<string, string | number>> {
    const table = data.map(el => {
        const row: Record<string, string | number> = {};
        const isBsad = el.fuelType === 'BSAD';
        row['Bid/Offer'] = isBid ? 'Bid' : 'Offer';
        row.Fuel = isBsad ? el.additionalBsadData?.fuelType ?? el.fuelType : el.fuelType;
        row['Is BSAD'] = isBsad ? 'TRUE' : 'FALSE';
        row.Unit = isBsad ? el.additionalBsadData?.assetId ?? el.plantId : el.plantId;
        row['BSAD Party'] = isBsad ? el.additionalBsadData?.partyId ?? '' : '';
        row.Price = formatNumber(el.mainPrice);
        row.Remaining = formatNumber(el.volume);
        row['Accepted unflagged'] = formatNumber(el.acceptedVolumes?.unflagged || 0);
        row['Accepted SO'] = el.soFlag ? formatNumber(el.acceptedVolumes?.soFlagged || 0) : 0;
        row['Accepted STOR'] = el.storFlag ? formatNumber(el.acceptedVolumes?.storFlagged || 0) : 0;
        row['Accepted CADL'] = el.cadlFlag ? formatNumber(el.acceptedVolumes?.cadlFlagged || 0) : 0;
        row.Type = el.bidOnTableTypeEnum;
        row.Cost = formatNumber(el.cost);
        row.ID = el.bodId;
        row.MNZT = el.mnzt;
        row.MZT = el.mzt;
        row.NDB = el.ndb;
        row.NDO = el.ndo;
        row.NDZ = el.ndz;
        row.SEL = el.sel;
        return row;
    });

    return table;
}

function getPeriodDataFilenameToDownload (periodObject: IDatePeriod): string {
    return `boa_data_${periodObject.date}_period_${periodObject.period}_at_${dayjs().get('hour')}_${dayjs().get('minute')}_${dayjs().get('second')}`;
}

function formatNumber (num: number): number {
    if (num != null) return num;
    return 0;
}

function areBoasEqual (boa1: IBoaIdentificationProperties, boa2: IBoaIdentificationProperties): boolean {
    return boa1.plantId === boa2.plantId && boa1.bodId === boa2.bodId && boa1.isBid === boa2.isBid;
}

const emptyBoaItem: IBoaItem = {
    plantId: '',
    isBid: 0,
    isAccepted: 0,
    volumeComparison: null,
    terreAccepted: null,
    offerPrice: 0,
    bidPrice: 0,
    terreFlag: false,
    soFlag: false,
    storFlag: false,
    cadlFlag: false,
    cost: 0,
    timeAccepted: '',
    enumType: 0,
    colour: '',
    fuelType: '',
    fuelEnum: 0,
    hasBoaWarning: false,
    tooltipOverride: null,
    isAnalytic: false,
    selected: false,
    show: false,
    theoretical: false,
    nonBmPrediction: false,
    dpForSummary: null,
    aggregated: false,
    allBoaInfoRaw: [],
    altBmuInfoRaw: [],
    allBoaReasons: '',
    additionalBsadData: null,
    isMissingBsad: false,
    sync: false,
    acceptedVolumes: {} as AcceptedVolumes,
    sel: 0,
    mzt: 0,
    ndb: 0,
    ndo: 0,
    ndz: 0,
    mnzt: 0,
    mdv: null,
    mdp: null,
    coords: [0, 0],
    bodId: '0',
    volume: 0,
    mainPrice: 0,
    bidOnTableTypeEnum: BidOnTableType.TurnUp,
    editedVolume: null,
    editedPrice: null,
    flagOverrides: [],
    maxAcceptedVolume: null,
    isBsad: false,
    _rowClass: '',
    _rowKey: '',
};

export {
    DEFAULT_BID_COLOUR,
    DEFAULT_OFFER_COLOUR,
    getFlagType,
    getAssetId,
    getBoaPlantDetailsId,
    isPlantBSAD,
    getTooltipInfo,
    formatCost,
    getBoaFormattedVolumeComparison,
    formatBoaVolume,
    getSyncFlagging,
    addOrUpdateDatatableTooltips,
    datePeriodIsNow,
    mergeBoas,
    markBoasAsUpdated,
    getBoaDashboardUpdateGroup,
    applyBoaDashboardUpdate,
    filterAvailableBoasToDisplay,
    getPeriodDataJson,
    getPeriodDataFilenameToDownload,
    emptyBoaItem,
    areBoasEqual,
};
